/* ── Geo-region configuration ─────────────────────────────────
 * Defines pricing, currency, compliance labels, and content
 * variants per region. Add new regions here as you expand.
 * ──────────────────────────────────────────────────────────── */

export type RegionCode = 'IN' | 'AE' | 'SA' | 'GLOBAL';

export interface RegionConfig {
  code: RegionCode;
  currency: string;
  currencySymbol: string;
  locale: string;              // Intl locale for number formatting
  pricing: {
    /** Standard plan: free for up to 5 users */
    standardExtra: number;     // per user/month from 6th user (monthly)
    standardExtraYearly: number; // per user/month from 6th user (yearly)
    /** Premium plan */
    premium: number;           // per user/month (monthly billing)
    premiumYearly: number;     // per user/month (yearly billing)
    /** Elite plan */
    elite: number;             // per user/month (monthly billing)
    eliteYearly: number;       // per user/month (yearly billing)
    proLabel: string;          // e.g. "/user/month"
  };
  compliance: string[];        // headline compliance features
  complianceLabel: string;     // short label for comparison table etc.
  /** Region-specific content strings */
  content: {
    trusted: string;           // "Trusted by 1,000+ companies in India"
    payrollFeature: string;    // for feature cards / free-account page
    entitySnippet: string;     // AEO entity paragraph region clause
  };
  /** Competitors shown in comparison table (empty = use global) */
  competitors?: {
    name: string;
    startingPrice: string;
  }[];
}

/* ── Region definitions ──────────────────────────────────────── */

const regions: Record<RegionCode, RegionConfig> = {
  IN: {
    code: 'IN',
    currency: 'INR',
    currencySymbol: '₹',
    locale: 'en-IN',
    pricing: { standardExtra: 55, standardExtraYearly: 45, premium: 70, premiumYearly: 60, elite: 220, eliteYearly: 200, proLabel: '/user/month' },
    compliance: ['PF', 'ESI', 'TDS', 'Professional Tax'],
    complianceLabel: 'Indian payroll compliance (PF, ESI, TDS)',
    content: {
      trusted: 'Trusted by 1,000+ companies across India',
      payrollFeature: 'India-ready payroll with PF, ESI, TDS auto-calculation',
      entitySnippet:
        'OfficePortal is trusted by over 1,000 companies across India and manages 50,000+ employees daily. It supports Indian statutory compliance including PF, ESI, TDS, and Professional Tax.',
    },
    competitors: [
      { name: 'Zoho People', startingPrice: '₹45/user' },
      { name: 'greytHR', startingPrice: '₹3,495/mo' },
      { name: 'Keka', startingPrice: '₹6,999/mo' },
    ],
  },

  AE: {
    code: 'AE',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-AE',
    pricing: { standardExtra: 3, standardExtraYearly: 2, premium: 5, premiumYearly: 4, elite: 8, eliteYearly: 6, proLabel: '/user/month' },
    compliance: ['WPS', 'MOHRE', 'Gratuity', 'SIF'],
    complianceLabel: 'UAE labour compliance (WPS, MOHRE)',
    content: {
      trusted: 'Trusted by 1,000+ companies worldwide',
      payrollFeature: 'UAE-ready payroll with WPS, gratuity & MOHRE compliance',
      entitySnippet:
        'OfficePortal is trusted by over 1,000 companies worldwide and manages 50,000+ employees daily. It supports UAE labour compliance including WPS, MOHRE reporting, gratuity calculations, and SIF.',
    },
    competitors: [
      { name: 'Bayzat', startingPrice: '$5/user' },
      { name: 'ZenHR', startingPrice: '$3/user' },
      { name: 'Zoho People', startingPrice: '$1.25/user' },
    ],
  },

  SA: {
    code: 'SA',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-SA',
    pricing: { standardExtra: 3, standardExtraYearly: 2, premium: 5, premiumYearly: 4, elite: 8, eliteYearly: 6, proLabel: '/user/month' },
    compliance: ['GOSI', 'WPS', 'Nitaqat', 'Muqeem'],
    complianceLabel: 'Saudi labour compliance (GOSI, WPS)',
    content: {
      trusted: 'Trusted by 1,000+ companies worldwide',
      payrollFeature: 'Saudi-ready payroll with GOSI, WPS & Nitaqat compliance',
      entitySnippet:
        'OfficePortal is trusted by over 1,000 companies worldwide and manages 50,000+ employees daily. It supports Saudi Arabia labour compliance including GOSI, WPS, Nitaqat, and Muqeem integration.',
    },
    competitors: [
      { name: 'Bayzat', startingPrice: '$5/user' },
      { name: 'ZenHR', startingPrice: '$3/user' },
      { name: 'Zoho People', startingPrice: '$1.25/user' },
    ],
  },

  GLOBAL: {
    code: 'GLOBAL',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    pricing: { standardExtra: 3, standardExtraYearly: 2, premium: 5, premiumYearly: 4, elite: 8, eliteYearly: 6, proLabel: '/user/month' },
    compliance: ['PF', 'ESI', 'TDS'],
    complianceLabel: 'Payroll compliance (PF, ESI, TDS & more)',
    content: {
      trusted: 'Trusted by 1,000+ companies worldwide',
      payrollFeature: 'Compliance-ready payroll with PF, ESI, TDS auto-calculation',
      entitySnippet:
        'OfficePortal is trusted by over 1,000 companies worldwide and manages 50,000+ employees daily.',
    },
  },
};

export default regions;

/* ── Helpers ── */

/** Map any ISO 3166-1 alpha-2 country code to our supported RegionCode */
export function countryToRegion(country: string | null | undefined): RegionCode {
  if (!country) return 'GLOBAL';
  const upper = country.toUpperCase();
  if (upper === 'IN') return 'IN';
  if (upper === 'AE') return 'AE';
  if (upper === 'SA') return 'SA';
  return 'GLOBAL';
}

/** Get the full config for a country code */
export function getRegionConfig(country: string | null | undefined): RegionConfig {
  return regions[countryToRegion(country)];
}

/** Format a price using the region's locale + currency */
export function formatPrice(amount: number, region: RegionConfig): string {
  if (amount === 0) return `${region.currencySymbol}0`;
  return new Intl.NumberFormat(region.locale, {
    style: 'currency',
    currency: region.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Pick the region-appropriate value for a comparison-table cell: the INR variant
 * for visitors whose region uses INR (India), otherwise the default (USD/global)
 * value. An empty INR value falls back to the default, so cells that only have a
 * single value are unaffected. Returns '' when nothing is set (the renderer then
 * shows a dash).
 */
export function pickGeoValue(
  defaultVal: string | null | undefined,
  inrVal: string | null | undefined,
  region: RegionConfig,
): string {
  if (region.currency === 'INR' && inrVal) return inrVal;
  return defaultVal ?? '';
}
